import { useCallback, useReducer } from "react";
import { BundleTypes, generateRandomUniqueId } from "../constants";
import { StandardConfigInterface, Action, ChangeEvents } from "../Types";

export const initialConfigData: StandardConfigInterface = {
    status: false,
    bundle_type: BundleTypes.STANDARD,
    bundle_as_product: {
        product_title: "",
        product_description: "",
        images: [],
        show_on_product_page: [],
        product_handle: "",
        double_discount: false,
        collections: [],
    },
    isProductPageVisible: false,
    // design from
    bundles_design: {
        default_cta: true,
        bundle_title_size: 22,
        bundle_title_color: "#000000",
        bundle_title_style: "650 normal",
        product_name_size: 14,
        product_name_color: "#050505",
        product_name_style: "450 normal",
        product_price_size: 11,
        product_price_color: "#050505",
        product_price_style: "350 normal",
        border_color: "#cccccc",
        border_width: 1,
        border_radius: 8,
        bundle_border_width: 0,
        product_card_seperator_width: 1,
        product_card_seperator_color: "#303030",
        bundle_border_color: "#cccccc",
        background_color: "#F2F4F2",
        highlighted_offer_tag_size: 12,
        highlighted_offer_tag_color: "#F8F8F8",
        highlighted_offer_tag_style: "550 italic",
        highlighted_price_tag_size: 12,
        highlighted_price_tag_color: "#050505",
        highlighted_price_tag_style: "750 normal",
        total_footer_text_size: 14,
        total_footer_text_color: "#000000",
        total_footer_text_style: "550 normal",
        total_footer_price_text_size: 16,
        total_footer_price_text_color: "#000000",
        total_footer_price_text_style: "750 normal",
        total_footer_background_color: "#f4f3f3",
        total_footer_background_style: "450 normal",
        product_background_color: "#FFFFFF",
        product_border_color: "#303030",
        add_to_cart_bg_color: "#303030",
        add_to_cart_border_radius: 8,
        add_to_cart_border_color: "#FFFFFF",
        add_to_cart_text_color: "#F5F5F5",
        add_to_cart_text_size: 14,
        add_to_cart_text_style: "750 normal",
        add_to_cart_border_width: 0,
        bundle_widget_bg: "#f4f4f400",
        bundle_widget_font_family: "Custom Store Font",
        product_img_size: "medium",
        product_img_show: "all",
        product_img_border_radius: 10,
        free_gift_header_bg_color: "#e3e3e3",
        free_gift_header_color: "#303030",
        free_gift_header_size: 13,
        free_gift_header_style: "650 normal",
        free_gift_bg_color: "#e0e0e0",
        free_gift_text_color: "#303030",
        free_gift_text_size: 12,
        free_gift_text_style: "650 normal",
        free_gift_border: true,
        free_gift_border_width: 0,
        free_gift_border_color: "#441144",
        product_img_border_width: 1,
        product_img_border_color: "#cccccc",
        variant_selector_text_size: 12,
        variant_selector_text_color: "#383838",
        variant_selector_text_style: "450 normal",
        swatches_select_color: "#000000",
    },
    allowed_product_pages: [],
    render_product_pages_type: false,
    show_on_no_pages: false,

    // content form
    bundle_name: "",
    title: "Bundle and Save more",
    description: "",
    discount_type: "percentage",
    discount_value: 5,
    order_discounts: true,
    product_discounts: true,
    shipping_discounts: true,
    products: [],
    volume_discounts: [],
    discounted_products: [],
    bundle_product_level: "product",
    hide_native_elements: "",
    add_to_cart_cta_text: "Add to Cart",
    quantity_text: "Qty",
    footer_total_text: "Total",
    footer_text: "Off",
    free_text: "Free",
    no_stock_text: "Out of stock",
    start_date: new Date().toISOString(),
    end_date: null,
    use_compare_at_price: false,
    apply_discount_on_compare_price: false,
    allow_free_gifts: false,
    free_gifts: [],
    free_gift_text: "+ Free Gift",
    selected_template_standard: "",
    bundle_embed_id: generateRandomUniqueId(),
    show_swatches: false,
    swatchOptions: [],
    swatchesProductsMap: {},
    swatchShape: "circle",
    swatchSize: 18,
    disable_product_title_link: false,
    offer_name: "",
    discount_label_template: "Bundle and Save more",
    show_unit_price: false,
    show_unit_price_text: "/unit",

    // target from
    targeting: {
        includeCustomers: "all",
        excludeCustomers: "none",
        includeCustomerData: "",
        excludeCustomerData: "",
        shopifyMarketsTargeting: "all",
        includeLocations: [],
        excludeLocations: [],
        shopifyMarketsTargetingData: [],
        //newKeys
        targetingType: "all",
        customerTagsEnabled: false,
        customerTagType: "include",
        customerTags: "",
        customerSpentEnabled: false,
        customerSpentType: "greater_than",
        customerSpent: "",
        customerCountriesEnabled: false,
        customerCountryType: "include",
        customerCountries: [],
    },

    // Settings
    general_settings: {
        show_bundle_on_product_page: false,
        show_multiple_bundles_on_product_page: false,
        redirect_after_adding_product_to_cart: "",
        redirect_to_checkout: false,
        show_powered_by_kefi: true,
        max_width: 1000,
        bundle_alignment: "flex-start",
        override_max_width: false,
        override_margin_top: false,
        margin_top: 20,
    },
    bundles_settings: {
        enable_stock_control: false,
        out_of_stock_text: "Out of stock",
        listing_variant_with_image: false,
        work_only_with_widget: false,
        bundle_position: "below_cart",
        custom_selector: "",
        custom_css: "",
        custom_js: "",
    },
    show_bundle_widget: "show_everywhere",
    free_shipping: false,
    animation_add_to_cart_btn: "none",
};

const useStandardHook = () => {
    const bundleConfigReducer = useCallback(
        (state: StandardConfigInterface, action: Action<StandardConfigInterface>): StandardConfigInterface => {
            switch (action?.type) {
                case ChangeEvents.initState:
                    return { ...state, ...action.init };
                default:
                    if (action?.key && action.isComplex) {
                        return {
                            ...state,
                            [action?.key]: {
                                ...(state[action.key] as object),
                                ...(action.payload as object),
                            },
                        };
                    } else if (action?.key) {
                        return { ...state, [action?.key]: action.payload };
                    } else return { ...state };
            }
        },
        [],
    );

    const [bundleConfig, dispatchBundleConfig] = useReducer(bundleConfigReducer, initialConfigData);

    return {
        bundleConfig,
        dispatchBundleConfig,
    };
};

export default useStandardHook;
